import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Auth } from '@angular/fire/auth';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, debounceTime, distinctUntilChanged, map, startWith } from 'rxjs';

import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { ShopKeys } from '@config/keys/shop.keys';
import type { IBlockConfig } from '@core/models/marketplace.model';
import { resolveParamRefTarget } from '@core/services/cb2-preview/runtime/content-builder-param-ref.util';
import {
  buildRuntimeContextSnapshot,
  resolveBoundValue,
  type RuntimeContextSnapshot,
} from '@core/services/cb2-preview/runtime/resolve-bound-value';
import { resolvePreviewBlocks } from '@core/services/cb2-preview/runtime/resolve-preview-blocks';
import {
  readVisibilityTestQueryFromUrl,
  resolveVisibilityTestMode,
} from '@core/services/content-builder/visibility-test-mode.util';
import { toShopRef } from '@core/services/link-navigation/link-navigation.service';
import { CONTENT_STORE_LOCAL_ACTIONS } from '@store/content/content.action';
import { SELECT_SHOP_CURRENT_PAGE } from '@store/shop/shop.selection';
import { DoodAppScreen } from '@store/order/order.model';

import { mergeContextualRuntimeBackingBlocks } from '@core/services/marketplace/marketplace-runtime.util';

import { Cb2CompiledRuntimeStateService } from './cb2-compiled-runtime-state.service';
import { JourneyParametersRuntimeService } from './journey-parameters-runtime.service';

export interface Cb2CompiledRuntimeInput {
  blocks: IBlockConfig[];
  paramValues?: Record<string, unknown>;
  paramCatalog?: Record<string, { type?: string }>;
}

function mapDeviceScreenToRuntimeDevice(screen: DoodAppScreen, isKiosk: boolean): string {
  if (isKiosk) {
    return 'kiosk';
  }
  if (screen === DoodAppScreen.desktop) {
    return 'desktop';
  }
  if (screen === DoodAppScreen.tablet) {
    return 'tablet';
  }
  return 'mobile';
}

function readPageShopId(shop: unknown): string | null {
  if (!shop || typeof shop !== 'object') {
    return null;
  }
  const id = (shop as Record<string, unknown>)[ShopKeys.Id];
  return typeof id === 'string' && id.trim() ? id.trim() : null;
}

@Injectable({ providedIn: 'root' })
export class Cb2ContextualRuntimeResolverService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly compiledRuntimeState = inject(Cb2CompiledRuntimeStateService);
  private readonly journeyParametersRuntime = inject(JourneyParametersRuntimeService);
  private readonly shopStoreSelector = inject(ShopStoreSelector);
  private readonly settingsStoreSelector = inject(SettingsStoreSelector);
  private readonly translate = inject(TranslateService);
  private readonly auth = inject(Auth);

  private readonly currentPageShop = this.store.selectSignal(SELECT_SHOP_CURRENT_PAGE);

  private contextWatchStarted = false;
  private lastAppliedSignature: string | null = null;
  private lastSourcesSignature: string | null = null;

  /**
   * Stores compiled runtime sources and resolves blocks + journey params for the
   * current snapshot. Does not push blocks to the store (initial boot handles that).
   */
  stageCompiledRuntime(
    input: Cb2CompiledRuntimeInput,
    contextOverrides?: Partial<
      Pick<RuntimeContextSnapshot, 'locale' | 'device' | 'shopId' | 'isAuthenticated' | 'isTestMode'>
    >,
  ): IBlockConfig[] {
    const sources = this.normalizeSources(input);
    const sourcesSignature = JSON.stringify({
      blocks: sources.blocks,
      paramValues: sources.paramValues,
    });
    if (sourcesSignature !== this.lastSourcesSignature) {
      this.lastSourcesSignature = sourcesSignature;
      this.lastAppliedSignature = null;
    }

    this.compiledRuntimeState.setSources(sources);
    this.ensureContextWatch();
    const context = buildRuntimeContextSnapshot({
      ...this.buildContextSnapshot(
        sources.paramValues,
        contextOverrides?.shopId ?? readPageShopId(this.currentPageShop()),
      ),
      ...contextOverrides,
    });
    const blocks = this.resolveBlocksForContext(sources, context);
    this.lastAppliedSignature = JSON.stringify(context);
    return blocks;
  }

  clear(): void {
    this.compiledRuntimeState.clear();
    this.lastAppliedSignature = null;
    this.journeyParametersRuntime.clear();
  }

  /** Preview / explicit snapshot — no NgRx block dispatch. */
  resolveWithSnapshot(
    partial: Partial<RuntimeContextSnapshot> & {
      locale: string;
      device: string;
      params?: Record<string, unknown>;
    },
    input?: Cb2CompiledRuntimeInput,
  ): IBlockConfig[] {
    const sources = input ? this.normalizeSources(input) : this.compiledRuntimeState.getSources();
    if (!sources) {
      return [];
    }
    const context = buildRuntimeContextSnapshot(partial);
    return this.resolveBlocksForContext(sources, context);
  }

  private ensureContextWatch(): void {
    if (this.contextWatchStarted) {
      return;
    }
    this.contextWatchStarted = true;

    combineLatest({
      locale: this.settingsStoreSelector.selectAppLocale.pipe(
        startWith(this.translate.currentLang ?? 'fr'),
      ),
      deviceScreen: this.settingsStoreSelector.selectDeviceScreen.pipe(startWith(DoodAppScreen.desktop)),
      isKiosk: this.settingsStoreSelector.selectKioskId.pipe(
        map(id => Boolean(id)),
        startWith(false),
      ),
      pageShop: this.shopStoreSelector.selectCurrentPage$.pipe(startWith(null)),
    })
      .pipe(
        debounceTime(0),
        map(({ locale, deviceScreen, isKiosk, pageShop }) =>
          this.buildContextSnapshot(
            this.compiledRuntimeState.getSources()?.paramValues ?? {},
            readPageShopId(pageShop),
            {
              locale,
              device: mapDeviceScreenToRuntimeDevice(deviceScreen, isKiosk),
            },
          ),
        ),
        distinctUntilChanged(
          (previous, next) => JSON.stringify(previous) === JSON.stringify(next),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(context => {
        const sources = this.compiledRuntimeState.getSources();
        if (!sources) {
          return;
        }
        this.applyRuntimeResolution(sources, context, true);
      });
  }

  private buildContextSnapshot(
    paramValues: Record<string, unknown>,
    shopId: string | null,
    overrides?: { locale?: string; device?: string },
  ): RuntimeContextSnapshot {
    return buildRuntimeContextSnapshot({
      locale: overrides?.locale ?? this.translate.currentLang ?? 'fr',
      device:
        overrides?.device ??
        mapDeviceScreenToRuntimeDevice(
          this.settingsStoreSelector.selectDeviceScreenSignal(),
          Boolean(this.settingsStoreSelector.deviceIsKiosk()),
        ),
      isAuthenticated: !!this.auth.currentUser,
      params: paramValues,
      shopId,
      isTestMode: resolveVisibilityTestMode(
        readVisibilityTestQueryFromUrl(window.location.href),
        false,
      ),
    });
  }

  private resolveBlocksForContext(
    sources: {
      blocks: IBlockConfig[];
      paramValues: Record<string, unknown>;
      paramCatalog: Record<string, { type?: string }>;
    },
    context: RuntimeContextSnapshot,
  ): IBlockConfig[] {
    const resolvedParams = this.resolveParamValuesMap(sources, context);
    this.journeyParametersRuntime.setParamValues(resolvedParams);

    return resolvePreviewBlocks(sources.blocks, {
      context,
      paramValues: sources.paramValues,
      paramCatalog: sources.paramCatalog,
      resolveActiveShop: () => toShopRef(this.shopStoreSelector.selectActive()),
    });
  }

  private applyRuntimeResolution(
    sources: {
      blocks: IBlockConfig[];
      paramValues: Record<string, unknown>;
      paramCatalog: Record<string, { type?: string }>;
    },
    context: RuntimeContextSnapshot,
    dispatchBlocks: boolean,
  ): IBlockConfig[] {
    const signature = JSON.stringify(context);
    if (signature === this.lastAppliedSignature) {
      return [];
    }
    this.lastAppliedSignature = signature;

    const blocks = this.resolveBlocksForContext(sources, context);
    if (dispatchBlocks && sources.blocks.length > 0) {
      this.store.dispatch(
        CONTENT_STORE_LOCAL_ACTIONS.setBlocks({
          blocks: mergeContextualRuntimeBackingBlocks(blocks),
        }),
      );
    }
    return blocks;
  }

  private resolveParamValuesMap(
    sources: {
      paramValues: Record<string, unknown>;
      paramCatalog: Record<string, { type?: string }>;
    },
    context: RuntimeContextSnapshot,
  ): Record<string, unknown> {
    const resolveParamRef = (refKey: string) =>
      resolveParamRefTarget(sources.paramValues, refKey, sources.paramCatalog);

    const out: Record<string, unknown> = {};
    for (const key of Object.keys(sources.paramValues)) {
      out[key] = resolveBoundValue(sources.paramValues[key], {
        context,
        lang: context.locale,
        resolveParamRef,
      });
    }
    return out;
  }

  private normalizeSources(input: Cb2CompiledRuntimeInput): {
    blocks: IBlockConfig[];
    paramValues: Record<string, unknown>;
    paramCatalog: Record<string, { type?: string }>;
  } {
    return {
      blocks: input.blocks ?? [],
      paramValues: input.paramValues ?? {},
      paramCatalog: input.paramCatalog ?? {},
    };
  }
}
